// ============================================================================
// pageBottomBar — a page's own bottom-bar modes (Design Constitution N02, Q09,
// Table C "Pasture", "Where the bar hides")
// ============================================================================
// Header stays a zero-prop module component (its overlay state relies on one
// component identity; global_nav_static pins it). A page that owns its modes —
// Pasture's Map / Field / Reports — or must hide the bar (Pasture Field mode)
// registers them here instead of passing props. The last registration wins and
// is removed when its page unmounts.
// ============================================================================
import {useEffect, useSyncExternalStore} from 'react';

import type {BottomTab} from './bottomBar.js';

export type PageBottomBar =
  | {hidden: true}
  | {hidden?: false; tabs: BottomTab[]; activeKey: string | null; onSelect: (key: string) => void};

let current: PageBottomBar | null = null;
let owner: symbol | null = null;
const listeners = new Set<() => void>();
const publish = () => listeners.forEach((l) => l());

export function setPageBottomBar(id: symbol, config: PageBottomBar | null): void {
  if (config === null) {
    if (owner !== id) return;
    owner = null;
    current = null;
  } else {
    owner = id;
    current = config;
  }
  publish();
}

export function getPageBottomBar(): PageBottomBar | null {
  return current;
}

export function subscribePageBottomBar(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Read by Header. */
export function usePageBottomBarConfig(): PageBottomBar | null {
  return useSyncExternalStore(subscribePageBottomBar, getPageBottomBar, getPageBottomBar);
}

/** Called by a page while it is mounted; `config` should be memoised by the caller. */
export function usePageBottomBar(config: PageBottomBar | null): void {
  useEffect(() => {
    const id = Symbol('page-bottom-bar');
    if (config) setPageBottomBar(id, config);
    return () => setPageBottomBar(id, null);
  }, [config]);
}
